import React, { useEffect, useRef, useState } from 'react';
import {
    Calendar, Folder, Wallet, LayoutGrid, Users, MessageSquare,
    Send, PenSquare, ScrollText, Zap, Settings, UserCog, Bell,
} from 'lucide-react';
import { avatarInitials } from '../utils/avatar';
import { useLanguage } from '../i18n';

/**
 * The PAI sidebar (the design system's fixed 250px left rail).
 *
 * Shared feature list — the sidebar AND the mobile overlay menu (Mobile Visual
 * #171) both render from this single source, so a new feature appears in both
 * places from one edit. Order is the design's, exactly.
 *
 * `workspace` (AI Personalization) is deliberately NOT here: the design's nav has
 * twelve entries and that is not one of them. The panel keeps its route, its
 * Cmd+6 shortcut and its entry point inside Settings.
 */
export const PANEL_BUTTONS = [
    { name: 'calendar', Icon: Calendar, label: 'Calendar' },
    { name: 'documents', Icon: Folder, label: 'File Manager' },
    { name: 'expenses', Icon: Wallet, label: 'Finance' }, // #193: display label; internal name/testid stay 'expenses'
    { name: 'boards', Icon: LayoutGrid, label: 'Boards' },
    { name: 'agenda', Icon: Users, label: 'Contacts' },
    { name: 'telegram', Icon: Send, label: 'Telegram' },
    // #437 split the old "Notes & Reminders" entry in two.
    { name: 'notes', Icon: PenSquare, label: 'Notes & Task List' },
    { name: 'reminders', Icon: Bell, label: 'Reminders & Notifications' },
    { name: 'rulebook', Icon: ScrollText, label: 'Rulebook' }, // PAI Rulebook #146
    { name: 'energy', Icon: Zap, label: 'Energy Tank' }, // Energy HUB part 1
];

/**
 * One nav row. The ACTIVE state's glowing white outline is a signature of this
 * design — it recurs on the focused input, the selected conversation row and the
 * send button. The 1.5px border is always present (transparent when idle) so a
 * row never shifts by a pixel when it becomes active.
 */
const NavRow = ({ active, label, accessibleName, onClick, testId, compact = false, unseen = false, children }) => (
    <button
        onClick={onClick}
        title={accessibleName || label}
        aria-label={accessibleName || label}
        data-testid={testId}
        data-active={active ? 'true' : 'false'}
        className={`relative w-full h-11 ${compact ? 'px-0 justify-center' : 'px-3.5'} rounded-row flex items-center gap-[13px] border-[1.5px] text-[15px] text-left transition-colors ${
            active
                ? 'border-white/80 bg-white/[0.055] shadow-glow font-semibold text-white'
                : 'border-transparent font-medium text-white/[0.78] hover:bg-white/[0.06]'
        }`}
    >
        <span className={`relative flex-none flex items-center ${active ? 'opacity-100' : 'opacity-[0.62]'}`}>
        {children}
        </span>
        {/* #437 — the red dot: something in this feature has not been seen yet.
            Drawn OUTSIDE the dimmed icon span so it keeps its full colour on an
            idle row, and absolutely placed so it never shifts the label. */}
        {unseen && (
            <span
                data-testid={`unseen-dot-${testId?.replace(/^nav-/, '')}`}
                aria-hidden="true"
                className="absolute w-2 h-2 rounded-full bg-pai-red"
                style={{ top: 9, left: compact ? 'calc(50% + 6px)' : 30 }}
            />
        )}
        {!compact && <span className="truncate">{label}</span>}
    </button>
);

/**
 * The account bubble.
 *
 * A deviation from the handoff, which draws the sidebar as logo + nav only: a
 * real session needs a visible account affordance, and Settings' ACCOUNT row is
 * too deep to be the only one. It sits below the nav in the design's own
 * language — a white initials circle, the same mark used for avatars throughout.
 */
const UserMenu = ({ user, onOpenUserSettings, compact = false }) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const initials = avatarInitials(user);

    return (
        <div className="relative" ref={ref}>
            {/* The avatar IS the circle, not a row around it: `rail-avatar` is the
                product's canonical identity mark, and specs read its computed
                background and its innerText as the single source of truth every
                other avatar surface must match (#109/#258). Wrapping it in a row
                would put the username inside that innerText. */}
            <div className="flex items-center gap-3 px-1">
            <button
                onClick={() => setOpen((v) => !v)}
                title={t('settingsTitle', 'Settings')}
                aria-label={t('navAccountMenu', 'Account menu')}
                data-testid="rail-avatar"
                    className={`w-11 h-11 flex-none rounded-full bg-white text-pai-void flex items-center justify-center text-xs font-extrabold select-none transition-shadow ${
                        open ? 'shadow-[0_0_18px_rgba(255,255,255,0.4)]' : 'hover:shadow-[0_0_18px_rgba(255,255,255,0.3)]'
                }`}
            >
                {initials}
            </button>
                {/* The visible display name beside the ball. Hot reload (#397)
                    gave it a testid: it is the account label a rename has to
                    move, and it is deliberately NOT inside `rail-avatar` (whose
                    innerText is the canonical initials mark — see above). */}
                <span
                    data-testid="rail-username"
                    className={`truncate text-[15px] font-medium text-white/[0.78] ${compact ? 'sr-only' : ''}`}
                >
                    {user?.username || t('navAccount', 'Account')}
                </span>
            </div>
            {open && (
                // Settings Update (#236): the profile popover is CLEAN — it exposes
                // ONLY the "Settings" navigation. Name/email + logout now live
                // exclusively INSIDE the User Settings view, so identity never leaks
                // into this popover.
                <div className="absolute left-0 bottom-full mb-2 z-50 w-52 rounded-ctl border border-pai bg-pai-modal shadow-modal-sm py-1 animate-fade-slide-in">
                    {onOpenUserSettings && (
                        <button
                            data-testid="rail-user-settings"
                            onClick={() => { setOpen(false); onOpenUserSettings(); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/[0.78] hover:bg-white/[0.06] hover:text-white transition-colors"
                        >
                            <UserCog size={14} />
                            {t('settingsTitle', 'Settings')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Fixed 250px sidebar at the left of the workspace shell: the PAI logo mark, the
 * twelve nav rows, and the account bubble.
 *
 * Clicking the logo returns to the launcher from any screen. Settings is the last
 * row and opens the SYSTEM view; the account bubble opens the USER view (#236 —
 * the two are structurally separate).
 *
 * **Indicators (#358).** `activePanels` is the SET of open surfaces, so the white
 * active outline can be on two rows at once — the workspace holds two occupants
 * (`utils/workspaceSlots.js`) and the sidebar must say which two. "Main Chat" is
 * one of those occupants rather than the base view: it lights up when the chat is
 * open and its row toggles it, exactly like every other row. The File Manager is
 * a modal overlay, so it lights up independently of the two-occupant cap.
 */
const IconRail = ({
    activePanels = null,
    onTogglePanel,
    onOpenSettings,
    onOpenLauncher,
    onToggleMainChat,
    user = null,
    compact = false,
    // #437: feature names with something unseen — drawn as a red dot.
    unseenPanels = [],
}) => {
    const { t } = useLanguage();
    const open = activePanels instanceof Set ? activePanels : new Set(activePanels || []);
    return (
            <div
            data-testid="icon-rail"
            data-compact={compact ? 'true' : 'false'}
            // #432: icon-only (72px) ONLY when two features must fit side by side on
            // a 1024–1065px viewport — see railMustCompact in utils/slotGeometry.js.
            // Every row keeps its testid and its accessible name (aria-label).
            className={`hidden shell:flex ${compact ? 'w-[72px]' : 'w-[250px]'} flex-none flex-col pai-panel pai-panel--sidebar ${compact ? 'px-2' : 'px-3.5'} pt-[22px] pb-[18px]`}
        >
            {/* Logo mark — returns to the launcher from any screen. */}
            <button
                onClick={() => onOpenLauncher?.()}
                title="PAI"
                aria-label={t('navLauncher', 'PAI — all features')}
                data-testid="rail-logo"
                className={`${compact ? 'w-12 h-12' : 'w-[58px] h-[58px]'} mx-auto mb-5 flex-none rounded-full bg-white text-pai-void flex items-center justify-center font-extrabold text-[18px] tracking-[-0.02em] select-none transition-shadow hover:shadow-[0_0_22px_rgba(255,255,255,0.4)]`}
            >
                PAI
            </button>

            {/* Nav */}
            <nav className="flex flex-col gap-[3px] flex-1 min-h-0 overflow-y-auto">
                <NavRow
                    active={open.has('chat')}
                    label={t('mainChat', 'Main Chat')}
                    onClick={() => onToggleMainChat?.()}
                    testId="nav-main-chat"
                    compact={compact}
                >
                    <MessageSquare size={20} />
                </NavRow>

                {PANEL_BUTTONS.map(({ name, Icon, label }) => (
                    <NavRow
                        key={name}
                        active={open.has(name)}
                        label={t(name, label)}
                        onClick={() => onTogglePanel?.(name)}
                        testId={`nav-${name}`}
                        compact={compact}
                        unseen={unseenPanels.includes(name)}
                    >
                        <Icon size={20} />
                    </NavRow>
                ))}

                <NavRow
                    active={false}
                    label={t('settings', 'Settings')}
                    accessibleName={`${t('systemSettings', 'System Settings')} (⌘,)`}
                    onClick={() => onOpenSettings?.('system')}
                    testId="nav-settings"
                    compact={compact}
                >
                    <Settings size={20} />
                </NavRow>
            </nav>

            <div className="mt-3 pt-3 border-t border-pai-hairline flex-none">
                <UserMenu user={user} compact={compact} onOpenUserSettings={() => onOpenSettings?.('user')} />
            </div>
        </div>
    );
};

export default IconRail;
